import { object, string, array, mixed, TestContext } from "yup";

import {
  ICompanyTypeSelectOption,
  IPlatformCompanySelectOption,
  IPrioritySelectOption,
} from "./Company";
import { IPipelineStageSelectOption } from "./Shared/Selects";
import { TypeOption, PersonValueItem } from "./Deal/Selects";
import { ICompanySelectOption, SourceCompanyOption } from "./Deal";
import { RelationObjectType } from "@enums/relations.enum";
import { IThemeOption } from "@interfaces/sourcing.interface";
import { IAttribute, IAttributeFieldValue } from "@interfaces/attribute.interface";
import { getEffectiveAttributeValue, validateAttributeValue } from "@components/Attributes";
import { UserForDropdown } from "@app-types/common.type";
import { IEmailAttachment } from "@interfaces/email.interface";
import { getErrorMessagesForDocuments } from "@utils/files.util";

export const URL_REGEX = /^(https?:\/\/)?(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/i;

// Max sourcing themes allowed on any entity (company, deal). Enforced via the yup schemas below
// (field-level RHF `rules` are ignored once a resolver is set) and surfaced by SourcingThemeFormField.
export const MAX_THEMES = 5;

// Attribute lists the `attribute_values` rules validate against; pass as `useForm({ context })`.
export type IAttributeValidationContext = {
  getAttributes: () => IAttribute[];
  // Only the deal dialog, which validates a deal's own attributes alongside the company's.
  getDealAttributes?: () => IAttribute[];
  // Mirrors the backend's `skip_required_attributes`; a getter because the form reads it live.
  skipRequiredAttributes?: () => boolean;
};

/** The context keys holding an attribute list — which one a given `attribute_values` array checks. */
type AttributeListSource = "getAttributes" | "getDealAttributes";

// Per-type attribute rules live in `validateAttributeValue`; they run here for the same reason as
// MAX_THEMES above — the `rules` on AttributesForm's Controller never fire alongside a resolver.
// `contentType` resolves the org's required flag, which is set per entity type.
function testAttributeValue(
  test: TestContext,
  fieldValue: IAttributeFieldValue | undefined,
  source: AttributeListSource,
  contentType: RelationObjectType
) {
  const context = test.options.context as IAttributeValidationContext | undefined;
  const attribute = context?.[source]?.().find((a) => a.id === fieldValue?.attribute);

  if (!attribute) return true;

  // In both lists means both entities are being filled in over one shared value, so each side's
  // required flag binds the other. The deal dialog empties the company list until one is picked.
  const isCommon = (["getAttributes", "getDealAttributes"] as const).every((key) =>
    context?.[key]?.().some((a) => a.id === attribute.id)
  );

  const value = fieldValue ? getEffectiveAttributeValue(fieldValue) : "";

  // An uploaded document is extracted into these later, so an empty one is allowed. Only the
  // required check is waived — a value the user did type still has to be valid.
  if (!value && context?.skipRequiredAttributes?.()) return true;

  const result = validateAttributeValue(attribute, value, contentType, isCommon);

  return result === true || test.createError({ message: result });
}

const attributeValuesSchema = (source: AttributeListSource, contentType: RelationObjectType) =>
  array()
    .of(
      mixed<IAttributeFieldValue>().test("attribute-value", function (fieldValue) {
        return testAttributeValue(this, fieldValue, source, contentType);
      })
    )
    .optional();

export const companySchema = object().shape({
  title: string().required("This field is required").min(3, "Title must be at least 3 characters"),
  website: string()
    .required("This field is required")
    .matches(URL_REGEX, "Please enter a complete website address"),
  priority: mixed<IPrioritySelectOption>().nullable().required("This field is required"),
  stage: mixed<IPipelineStageSelectOption>().required("This field is required"),
  type: mixed<ICompanyTypeSelectOption>().nullable().required("This field is required"),
  users: array().of(mixed<UserForDropdown>()).optional(),
  themes: array()
    .of(mixed<IThemeOption>())
    .max(MAX_THEMES, `Only ${MAX_THEMES} themes allowed`)
    .optional(),
  platform: mixed<IPlatformCompanySelectOption>().nullable().optional(),
  attribute_values: attributeValuesSchema("getAttributes", RelationObjectType.COMPANY),
});

export const dealSchema = object().shape({
  title: string().required("This field is required").min(3, "Title must be at least 3 characters"),
  stage: mixed<IPipelineStageSelectOption>().required("This field is required"),
  company: mixed<ICompanySelectOption>().nullable().optional(),
  source_type: mixed<TypeOption>().nullable().optional(),
  source_company: mixed<SourceCompanyOption>().nullable().optional(),
  source_people: object()
    .shape({
      person: array().of(mixed<PersonValueItem>()).optional(),
      user: array().of(mixed<PersonValueItem>()).optional(),
    })
    .optional(),
  users: array().of(mixed<UserForDropdown>()).optional(),
  themes: array()
    .of(mixed<IThemeOption>())
    .max(MAX_THEMES, `Only ${MAX_THEMES} themes allowed`)
    .optional(),
  documents: array()
    .of(mixed<File>())
    .test("valid-documents", "Documents can't be uploaded", (files) => {
      const errors = getErrorMessagesForDocuments(files ?? []);
      return errors.length === 0;
    })
    .optional(),
  attachments: array().of(mixed<IEmailAttachment>()).optional(),
  attribute_values: attributeValuesSchema("getAttributes", RelationObjectType.COMPANY),
  deal_attribute_values: attributeValuesSchema("getDealAttributes", RelationObjectType.DEAL),
});
